import type {
  LibraryBreadcrumb,
  LibraryContent,
  LibraryContentStatus,
  LibraryContentType,
  LibraryFolder,
} from '../types';
import type { QueryClient } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import FetchPromise from '~/agent/fetch-promise';
import { LIBRARY_API_ROOT_URL } from '../constants';

export interface GetLibraryContentResponse {
  content: LibraryContent[];
  folderAncestors: LibraryBreadcrumb[];
  folder?: LibraryFolder;
}

const getLibraryContent = (
  id: string,
  isGlobalSearch: boolean,
  isLearnerView: boolean,
  folderId?: string,
  query?: string,
  contentTypes?: LibraryContentType[],
  tags?: string[],
  contentStatus?: LibraryContentStatus[],
  skills?: string[],
): Promise<GetLibraryContentResponse> => {
  const url = `${LIBRARY_API_ROOT_URL}/${id}/content`;
  const params = new URLSearchParams();
  if (folderId) {
    params.set('university_folder_id', folderId);
  }
  if (query) {
    params.set('query', query);
  }
  if (contentTypes) {
    contentTypes.forEach((t) => {
      params.append('content_type[]', t);
    });
  }
  if (tags) {
    tags.forEach((t) => {
      params.append('tags[]', t);
    });
  }
  if (contentStatus) {
    contentStatus.forEach((s) => {
      params.append('status[]', s);
    });
  }
  if (skills) {
    skills.forEach((s) => {
      params.append('skills[]', s);
    });
  }

  params.set('is_learner_view', isLearnerView.toString());

  // need to add a timestamp to avoid caching
  // when a user return from a preview page we want to make sure we get the latest metrics
  const unixTimestamp = Date.now().toString();
  params.append('timestamp', unixTimestamp);
  params.append('is_global_search', isGlobalSearch ? isGlobalSearch.toString() : 'false');
  const paramsStr = params.toString() ? `?${params.toString()}` : '';
  return FetchPromise.get(`${url}${paramsStr}`);
};

export const GET_LIBRARY_KEY = 'get-library-content';

export const useGetLibraryContent = ({
  id,
  folderId,
  isLearnerView,
  isGlobalSearch,
  query,
  contentTypes,
  tags,
  contentStatus,
  skills,
}: {
  id: string;
  folderId?: string;
  isLearnerView: boolean;
  isGlobalSearch?: boolean;
  query?: string;
  contentTypes?: LibraryContentType[];
  tags?: string[];
  contentStatus?: LibraryContentStatus[];
  skills?: string[];
}) =>
  useQuery<GetLibraryContentResponse, JQuery.jqXHR>(
    [GET_LIBRARY_KEY, id, folderId, query, contentTypes, tags, contentStatus, skills],
    () =>
      getLibraryContent(id, isGlobalSearch, isLearnerView, folderId, query, contentTypes, tags, contentStatus, skills),
  );

export const clearGetLibraryContentCache = async (queryClient: QueryClient) => {
  const keyToInvalidate = [GET_LIBRARY_KEY];
  await queryClient.invalidateQueries(keyToInvalidate);
};
